import { useCallback, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Part A is a blocker for company_admin until part_a_completed_at is set. Superadmin impersonation bypasses it. */
export function partANeedsModal(user, { impersonating } = {}) {
  if (!user || user.role !== 'company_admin') return false;
  const isImpersonating = impersonating != null
    ? impersonating
    : (user.impersonated_by || (typeof localStorage !== 'undefined' && localStorage.getItem('original_superadmin_token') !== null));
  if (isImpersonating) return false;
  if (user.part_a_completed === true) return false;
  const hub = user.setup_hub;
  if (!hub) return false; // legacy session without setup_hub: never block on missing data
  return !hub.part_a_completed_at;
}

// Backwards-compatible name used by the dashboard.
export const hubNeedsModal = partANeedsModal;

export function useOnboardingHub() {
  const [partA, setPartA] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPartA = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/onboarding-hub/part-a`, { headers: authHeaders() });
      setPartA(res.data);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load company setup');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const savePartA = useCallback(async (payload) => {
    const res = await axios.put(`${API}/onboarding-hub/part-a`, payload, { headers: authHeaders() });
    setPartA((prev) => ({ ...(prev || {}), ...res.data }));
    return res.data;
  }, []);

  const completePartA = useCallback(async (payload) => {
    const res = await axios.post(`${API}/onboarding-hub/part-a/complete`, payload, { headers: authHeaders() });
    setPartA((prev) => ({ ...(prev || {}), ...res.data }));
    return res.data;
  }, []);

  const loadProgress = useCallback(async () => {
    const res = await axios.get(`${API}/onboarding-hub/progress`, { headers: authHeaders() });
    return res.data;
  }, []);

  const nextUndone = useCallback(async () => {
    const res = await axios.get(`${API}/onboarding-hub/next-undone`, { headers: authHeaders() });
    return res.data.page;
  }, []);

  const seedSampleData = useCallback(async () => {
    const res = await axios.post(`${API}/onboarding-hub/sample-data`, {}, { headers: authHeaders() });
    return res.data;
  }, []);

  const removeSampleData = useCallback(async () => {
    const res = await axios.delete(`${API}/onboarding-hub/sample-data`, { headers: authHeaders() });
    return res.data;
  }, []);

  const chooseRealData = useCallback(async () => {
    await axios.post(`${API}/onboarding-hub/data-mode/real`, {}, { headers: authHeaders() });
  }, []);

  return {
    partA,
    setPartA,
    loading,
    error,
    loadPartA,
    savePartA,
    completePartA,
    loadProgress,
    nextUndone,
    seedSampleData,
    removeSampleData,
    chooseRealData,
    authHeaders,
    API,
  };
}
